// Hook for calling the homebaseOrchestrateV3 Firebase function
import { useState, useCallback, useRef } from 'react';

function mergePlanningPayload(previous, payload) {
  if (!previous || !payload?.success) return previous;
  return {
    ...previous,
    travelDates: payload.travelDates,
    partyProfile: payload.partyProfile,
    experience: payload.experience,
    misc: payload.misc,
    planningSynthesesReady: true
  };
}

export const useHomebaseGenerate = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [planningError, setPlanningError] = useState(null);

  // Track in-flight requests by interviewId to prevent duplicate calls
  const inflightRef = useRef(new Map());
  const planningInflightRef = useRef(new Map());

  const ensurePlanningSyntheses = useCallback(async (interviewId) => {
    if (!interviewId) return;

    const existing = planningInflightRef.current.get(interviewId);
    if (existing) {
      setPlanningLoading(true);
      return existing;
    }

    const planningPromise = (async () => {
      setPlanningLoading(true);
      setPlanningError(null);
      try {
        console.log('[useHomebaseGenerate] Making HTTP request to homebasePlanningSynthesesV2 for:', interviewId);

        const planningController = new AbortController();
        const planningTimeoutId = setTimeout(() => planningController.abort(), 60000);

        const planningResponse = await fetch('https://us-central1-alakaialohaadventures-782c9.cloudfunctions.net/homebasePlanningSynthesesV2', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: { interviewId } }),
          signal: planningController.signal
        });

        clearTimeout(planningTimeoutId);

        console.log('[useHomebaseGenerate] Planning response received:', planningResponse.status, planningResponse.statusText);

        if (!planningResponse.ok) {
          const errorText = await planningResponse.text();
          console.error('[useHomebaseGenerate] Planning error response:', errorText);
          throw new Error(`HTTP error! status: ${planningResponse.status}, body: ${errorText}`);
        }

        const planningResponseData = await planningResponse.json();
        console.log('[useHomebaseGenerate] Planning response data:', planningResponseData);

        const planningResult = { data: planningResponseData };
        const payload = planningResult.data;

        if (!payload?.success) {
          setPlanningError(payload?.error || 'Failed to finalize planning syntheses');
          return planningResult;
        }

        setData((prev) => mergePlanningPayload(prev, payload));

        return planningResult;
      } catch (planningErr) {
        console.error('Planning syntheses error:', planningErr);
        setPlanningError(planningErr.message || 'Failed to generate planning syntheses');
        throw planningErr;
      } finally {
        setPlanningLoading(false);
        planningInflightRef.current.delete(interviewId);
      }
    })();

    planningInflightRef.current.set(interviewId, planningPromise);
    return planningPromise;
  }, []);

  const generateHomebase = useCallback(async (interviewId) => {
    if (!interviewId) {
      setError('Interview ID is required');
      return;
    }

    if (inflightRef.current.has(interviewId)) {
      return inflightRef.current.get(interviewId);
    }

    setLoading(true);
    setError(null);
    setPlanningError(null);
    setPlanningLoading(false);
    setData(null);

    const p = (async () => {
      // These syntheses do not depend on the homebase recommendation. Starting
      // both requests together removes an avoidable second wait before the CTA
      // becomes available. The synthesis endpoint reuses an existing ready set.
      const planningPromise = ensurePlanningSyntheses(interviewId).catch(() => null);

      try {
        // Call as HTTP function instead of callable
        console.log('[useHomebaseGenerate] Making HTTP request to homebaseOrchestrateV3 for:', interviewId);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

        const response = await fetch('https://us-central1-alakaialohaadventures-782c9.cloudfunctions.net/homebaseOrchestrateV3', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: { interviewId } }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log('[useHomebaseGenerate] Response received:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[useHomebaseGenerate] Error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }

        const responseData = await response.json();
        console.log('[useHomebaseGenerate] Response data:', responseData);

        const result = { data: responseData };

        if (!result.data.success) {
          setError(result.data.error || 'Failed to generate homebase');
          return result;
        }

        setData(result.data);

        if (!result.data.planningSynthesesReady) {
          // Merge once more after the homebase payload is installed. This also
          // handles the race where the faster synthesis request finished first.
          void planningPromise.then((planningResult) => {
            setData((prev) => mergePlanningPayload(prev, planningResult?.data));
          });
        } else {
          // A previously generated ready set remains usable even if the
          // background freshness check fails.
          void planningPromise.then(() => setPlanningError(null));
        }

        return result;
      } catch (err) {
        console.error('Homebase generation error:', err);
        setError(err.message || 'An unexpected error occurred');
        throw err;
      } finally {
        setLoading(false);
        inflightRef.current.delete(interviewId);
      }
    })();

    inflightRef.current.set(interviewId, p);
    return p;
  }, [ensurePlanningSyntheses]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setPlanningError(null);
    setPlanningLoading(false);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    planningLoading,
    error,
    planningError,
    generateHomebase,
    ensurePlanningSyntheses,
    reset
  };
};
